import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { useToast } from '../../hooks/use-toast';
import api, { formatApiError } from '../../lib/api';
import { Loader2, RefreshCw, Waves, Receipt, TrendingUp } from 'lucide-react';

const inr = (n) => '\u20B9' + (Number(n) || 0).toLocaleString('en-IN', { maximumFractionDigits: 2 });
const monthStart = () => { const d = new Date(); return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`; };
const todayStr = () => new Date().toISOString().slice(0, 10);

const CoachPayoutPage = () => {
  const [data, setData] = useState(null);
  const [start, setStart] = useState(monthStart());
  const [end, setEnd] = useState(todayStr());
  const [loading, setLoading] = useState(true);
  const { toast } = useToast();
  const ref = useRef({ start, end });
  ref.current = { start, end };

  const load = useCallback(async (silent = false) => {
    if (!silent) setLoading(true);
    try {
      const res = await api.get('/analytics/coach-payout', { params: { start: ref.current.start, end: ref.current.end } });
      setData(res.data);
    } catch (err) { if (!silent) toast({ title: 'Failed to load', description: formatApiError(err), variant: 'destructive' }); }
    finally { setLoading(false); }
  }, [toast]);

  useEffect(() => { load(); }, [load, start, end]);
  useEffect(() => { const t = setInterval(() => load(true), 20000); return () => clearInterval(t); }, [load]);

  return (
    <div data-testid="admin-coach-payout">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Coach Payout</h1>
          <p className="text-gray-500 text-sm mt-1">(Training sales − 19.5%) × 20% · single pool · excludes self-swimming, day passes & accessories</p>
        </div>
        <div className="flex items-end gap-2">
          <div><Label className="text-xs">From</Label><Input type="date" value={start} max={end} onChange={(e) => setStart(e.target.value)} className="h-9" data-testid="payout-start" /></div>
          <div><Label className="text-xs">To</Label><Input type="date" value={end} min={start} max={todayStr()} onChange={(e) => setEnd(e.target.value)} className="h-9" data-testid="payout-end" /></div>
          <Button variant="outline" size="icon" className="h-9 w-9" onClick={() => load()} data-testid="payout-refresh"><RefreshCw className="h-4 w-4" /></Button>
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-6">
        <Card className="border-0 shadow-sm bg-gradient-to-br from-teal-600 to-emerald-600 text-white"><CardContent className="p-5"><div className="flex items-center justify-between"><div><p className="text-sm opacity-90">Coach Payout Pool</p><p className="text-3xl font-bold mt-1" data-testid="payout-total">{inr(data?.total_payout)}</p></div><Waves className="h-8 w-8 opacity-80" /></div></CardContent></Card>
        <Card className="border-0 shadow-sm"><CardContent className="p-5"><div className="flex items-center justify-between"><div><p className="text-sm text-gray-500">Training Sales</p><p className="text-2xl font-bold text-gray-900 mt-1">{inr(data?.total_training_sales)}</p></div><Receipt className="h-7 w-7 text-gray-300" /></div></CardContent></Card>
        <Card className="border-0 shadow-sm"><CardContent className="p-5"><div className="flex items-center justify-between"><div><p className="text-sm text-gray-500">Net after 19.5%</p><p className="text-2xl font-bold text-gray-900 mt-1">{inr(data?.total_net)}</p></div><TrendingUp className="h-7 w-7 text-gray-300" /></div></CardContent></Card>
      </div>

      <Card className="shadow-md border-0 bg-white">
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-16"><Loader2 className="h-8 w-8 animate-spin text-teal-500" /></div>
          ) : !data?.breakdown?.length ? (
            <div className="text-center py-16 text-gray-500" data-testid="payout-empty">No training sales in this period.</div>
          ) : (
            <Table data-testid="payout-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Training Package</TableHead>
                  <TableHead className="text-right">Count</TableHead>
                  <TableHead className="text-right">Sales</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.breakdown.map((b) => (
                  <TableRow key={b.plan_name} data-testid={`payout-row-${b.plan_name}`}>
                    <TableCell className="font-medium">{b.plan_name}</TableCell>
                    <TableCell className="text-right text-gray-600">{b.count}</TableCell>
                    <TableCell className="text-right">{inr(b.sales)}</TableCell>
                  </TableRow>
                ))}
                <TableRow className="bg-gray-50 font-semibold">
                  <TableCell>Total</TableCell>
                  <TableCell className="text-right">{data.breakdown.reduce((s, b) => s + b.count, 0)}</TableCell>
                  <TableCell className="text-right">{inr(data.total_training_sales)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default CoachPayoutPage;
